import {
  Autocomplete,
  Checkbox,
  FormControl,
  IconButton,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { Leader } from '../../store/types'
import { Clear, Verified, VerifiedOutlined } from '@mui/icons-material'
import { Control, Controller } from 'react-hook-form'
import { t } from 'i18next'
import { ProjectFormValues } from '../../store/types/form/ProjectFormValues'
import { useIsOnlyProjectLeader } from '../../store/redux/hooks'
import sortByField from '../../store/utils/sortByField'

type SelectProjectLeadersProps = {
  leaders: Leader[]
  control: Control<ProjectFormValues, any>
}

const SelectProjectLeaders = ({ leaders, control }: SelectProjectLeadersProps) => {
  const isOnlyProjectLeader = useIsOnlyProjectLeader()
  return (
    <FormControl>
      <Controller
        name={'leaders'}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <Stack>
              {!isOnlyProjectLeader ? (
                <Autocomplete
                  disabled={isOnlyProjectLeader}
                  multiple
                  disablePortal
                  disableClearable
                  disableCloseOnSelect
                  filterSelectedOptions
                  value={value}
                  onChange={(event, value) => onChange(sortByField(value, 'lastName'))}
                  renderInput={params => (
                    <TextField
                      {...(params as any)}
                      size={'small'}
                      label={t('components.selectProjectLeaders.leadersLabel')}
                      error={!!error}
                      helperText={error ? 'error' : undefined}
                    />
                  )}
                  options={leaders.map(leader => ({ ...leader, main: leader.main ?? false }))}
                  noOptionsText={t('components.selectProjectLeaders.noMoreLeaders')}
                  getOptionLabel={option => option.firstName + ' ' + option.lastName}
                  renderOption={(props, option) => (
                    <ListItem {...props} sx={{ gap: 1 }} key={option.id}>
                      {`${option.firstName} ${option.lastName}`}
                    </ListItem>
                  )}
                  renderTags={() => null}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              ) : (
                <Tooltip title={t('page.projects.form.assignedLeaders.tooltip')} arrow placement={'right'}>
                  <Typography sx={{ width: 'fit-content' }} variant={'h6'}>
                    {t('page.projects.form.assignedLeaders.text')}
                  </Typography>
                </Tooltip>
              )}
              {value?.map(leader => {
                return (
                  <ListItem key={leader.id} sx={{ pl: 0, pr: '6px' }}>
                    <Checkbox
                      disabled={isOnlyProjectLeader}
                      icon={<VerifiedOutlined />}
                      checkedIcon={<Verified />}
                      checked={leader.main ?? false}
                      onChange={(event, checked) => {
                        const updatedLeaders = value.map(l =>
                          l.id === leader.id ? { ...l, main: checked } : { ...l, main: false }
                        )
                        onChange(updatedLeaders)
                      }}
                      inputProps={{
                        'aria-label': `checkbox-main-leader-${leader.firstName}-${leader.lastName}`,
                      }}
                    />
                    {leader.firstName + ' ' + leader.lastName}
                    {!isOnlyProjectLeader && (
                      <IconButton
                        aria-label={`remove-${leader.firstName}-${leader.lastName}-from-project-leaders-button`}
                        sx={{ ml: 'auto' }}
                        size="small"
                        onClick={() => {
                          const updatedLeaders = value.filter(l => l.id !== leader.id)
                          onChange(updatedLeaders)
                        }}
                      >
                        <Clear sx={{ height: 16, width: 16 }} />
                      </IconButton>
                    )}
                  </ListItem>
                )
              })}
            </Stack>
          )
        }}
      />
    </FormControl>
  )
}

export default SelectProjectLeaders
